import { memo, useEffect } from 'react';
import useRefCallback from '../../hooks/useRefCallback';
import { TColorTheme, TColorThemeChangedByType } from './colorTheme.t';
import { useDispatch, useSelector } from 'react-redux';
import { getUserThemeSelector } from './colorThemeSelectors';
import { COLOR_THEME_CHANGED_BY_TYPES, COLOR_THEME_NAMES, SYSTEM_COLOR_SCHEMES } from './colorTheme.constants';
import { getAppFeatures } from '../../app/appSelectors';
import { FEATURES, LOG_AF_EVENT_NAMES } from '../../app/appConstants';
import { logAppsflyerAction } from '../../app/appActions';
import { colorThemeSlice } from './colorThemeReducer';

function ColorThemeProviderBase() {
    const userTheme = useSelector(getUserThemeSelector);
    const dispatch = useDispatch();

    const setAppColorTheme = useRefCallback((theme: Extract<TColorTheme, 'light' | 'dark'>, changedBy: TColorThemeChangedByType) => {
        const html = document.querySelector('html');
        if (!html) return;
        html.setAttribute('data-theme', theme);
        dispatch(colorThemeSlice.actions.setAppThemeAction(theme));
        dispatch(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.COLOR_THEME_CHANGE,
            payload: { theme, changed_by: changedBy },
        }));
    });

    useEffect(() => {
        if (!window.matchMedia || userTheme !== COLOR_THEME_NAMES.SYSTEM) {
            return;
        }

        const lightMatch = window.matchMedia(SYSTEM_COLOR_SCHEMES.LIGHT);
        lightMatch.onchange = (e) => {
            if (e.matches) {
                setAppColorTheme(COLOR_THEME_NAMES.LIGHT, COLOR_THEME_CHANGED_BY_TYPES.SYSTEM);
            } else {
                setAppColorTheme(COLOR_THEME_NAMES.DARK, COLOR_THEME_CHANGED_BY_TYPES.SYSTEM);
            }
        };
    }, [setAppColorTheme, userTheme]);

    useEffect(() => {
        if (!window.matchMedia || userTheme !== COLOR_THEME_NAMES.SYSTEM) {
            return;
        }
        if (window.matchMedia(SYSTEM_COLOR_SCHEMES.DARK).matches) {
            return setAppColorTheme(COLOR_THEME_NAMES.DARK, COLOR_THEME_CHANGED_BY_TYPES.SYSTEM);
        }
        if (window.matchMedia(SYSTEM_COLOR_SCHEMES.LIGHT).matches) {
            return setAppColorTheme(COLOR_THEME_NAMES.LIGHT, COLOR_THEME_CHANGED_BY_TYPES.SYSTEM);
        }
    }, [userTheme, setAppColorTheme]);

    useEffect(() => {
        if (userTheme === COLOR_THEME_NAMES.SYSTEM) return;
        setAppColorTheme(userTheme, COLOR_THEME_CHANGED_BY_TYPES.USER);
    }, [dispatch, userTheme, setAppColorTheme]);

    return null;
}

function ColorThemeProvider() {
    const features = useSelector(getAppFeatures);

    if (!features[FEATURES.COLOR_THEME]) return null;

    return <ColorThemeProviderBase/>;
}

export default memo(ColorThemeProvider);
